import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import './AdminWallets.css';

type WalletUser = {
  id: string;
  name: string;
  email: string;
  balance: number;
};

type Transaction = {
  id: string;
  type: 'FEE' | 'TOPUP' | 'PURCHASE' | 'REFUND' | 'CASHBACK';
  amount: number;
  description: string;
  paymentMethod?: string;
  status?: 'pending' | 'completed' | 'rejected';
  createdAt: string;
};

const paymentOptions = [
  { value: 'cash', label: 'Cash' },
  { value: 'etransfer', label: 'E-Transfer' },
  { value: 'card', label: 'Card' },
  { value: 'bank', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const AdminWallets: React.FC = () => {
  const [users, setUsers] = useState<WalletUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<WalletUser | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'TOPUP' | 'REFUND' | 'PURCHASE' | 'FEE'>('TOPUP');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const term = search.toLowerCase();
    return users.filter(user =>
      `${user.name} ${user.email}`.toLowerCase().includes(term)
    );
  }, [search, users]);

  const fetchUsers = async (query?: string) => {
    const response = await api.get('/admin/wallets/users', {
      params: query ? { search: query } : undefined,
    });
    setUsers(response.data?.users || []);
  };

  const fetchUserWallet = async (userId: string) => {
    setDetailLoading(true);
    try {
      const response = await api.get(`/admin/wallets/${userId}`);
      const user = response.data?.user;
      const balance = typeof response.data?.balance === 'number' ? response.data.balance : user?.balance ?? 0;
      setSelectedUser(user ? { ...user, balance } : null);
      setTransactions(response.data?.transactions || []);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        await fetchUsers();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleSelectUser = async (user: WalletUser) => {
    setSelectedUser(user);
    await fetchUserWallet(user.id);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedUser) return;
    const parsed = Number(amount);
    if (Number.isNaN(parsed) || parsed <= 0) return;

    setSaving(true);
    try {
      await api.post(`/admin/wallets/${selectedUser.id}/transactions`, {
        type,
        amount: parsed,
        paymentMethod,
        note: note.trim() || undefined,
      });
      setAmount('');
      setNote('');
      await fetchUsers(search || undefined);
      await fetchUserWallet(selectedUser.id);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (transactionId: string) => {
    if (!selectedUser) return;
    setApproving(transactionId);
    try {
      await api.post(`/admin/wallets/${selectedUser.id}/transactions/${transactionId}/approve`);
      await fetchUsers(search || undefined);
      await fetchUserWallet(selectedUser.id);
    } finally {
      setApproving(null);
    }
  };

  return (
    <div className="container admin-wallets-page">
      <div className="admin-wallets-header">
        <div>
          <h1>Wallet Manager</h1>
          <p>Top up or adjust wallet balances for members.</p>
        </div>
        <div className="admin-wallets-search">
          <input
            type="text"
            placeholder="Search by name or email"
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
      </div>

      <div className="admin-wallets-layout">
        <aside className="wallet-user-list">
          {loading ? (
            <div className="wallet-skeleton">
              {[0, 1, 2].map((item) => (
                <div key={item} className="wallet-skeleton-card">
                  <div className="wallet-skeleton-line title" />
                  <div className="wallet-skeleton-line meta" />
                  <div className="wallet-skeleton-line meta short" />
                </div>
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="wallet-empty">
              <div className="wallet-empty-icon">👥</div>
              <h3>No users found</h3>
              <p>Try a different name or email.</p>
              <button type="button" className="btn btn-secondary" onClick={() => setSearch('')}>
                Clear search
              </button>
            </div>
          ) : (
            filteredUsers.map(user => (
              <button
                key={user.id}
                className={`wallet-user-item ${selectedUser?.id === user.id ? 'active' : ''}`}
                onClick={() => handleSelectUser(user)}
              >
                <div className="wallet-user-name">{user.name}</div>
                <div className="wallet-user-email">{user.email}</div>
                <div className="wallet-user-balance">${Number(user.balance || 0).toFixed(2)}</div>
              </button>
            ))
          )}
        </aside>

        <section className="wallet-detail">
          {selectedUser ? (
            detailLoading ? (
              <div className="wallet-detail-skeleton">
                <div className="wallet-detail-skeleton-header">
                  <div className="wallet-skeleton-line title" />
                  <div className="wallet-skeleton-line meta short" />
                </div>
                <div className="wallet-skeleton-card">
                  <div className="wallet-skeleton-line title" />
                  <div className="wallet-skeleton-line meta" />
                  <div className="wallet-skeleton-line meta short" />
                </div>
                <div className="wallet-skeleton-card">
                  <div className="wallet-skeleton-line title" />
                  <div className="wallet-skeleton-line meta" />
                  <div className="wallet-skeleton-line meta" />
                </div>
              </div>
            ) : (
              <>
              <div className="wallet-detail-header">
                <div>
                  <h2>{selectedUser.name}</h2>
                  <p>{selectedUser.email}</p>
                </div>
                <div className="wallet-balance-pill">
                  Balance ${Number(selectedUser.balance || 0).toFixed(2)}
                </div>
              </div>

              <form className="wallet-action-card" onSubmit={handleSubmit}>
                <h3>New Transaction</h3>
                <div className="wallet-action-grid">
                  <div className="form-group">
                    <label>Type</label>
                    <select value={type} onChange={event => setType(event.target.value as any)}>
                      <option value="TOPUP">Top-up</option>
                      <option value="REFUND">Refund</option>
                      <option value="PURCHASE">Debit</option>
                      <option value="FEE">Fee</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={amount}
                      onChange={event => setAmount(event.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Method</label>
                    <select
                      value={paymentMethod}
                      onChange={event => setPaymentMethod(event.target.value)}
                    >
                      {paymentOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Note</label>
                    <input
                      type="text"
                      value={note}
                      onChange={event => setNote(event.target.value)}
                      placeholder="Optional note"
                    />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Add Transaction'}
                </button>
              </form>

              <div className="wallet-transactions">
                <h3>Recent Transactions</h3>
                {transactions.length === 0 ? (
                  <div className="wallet-transactions-empty">
                    <div className="wallet-empty-icon">🧾</div>
                    <h4>No transactions yet</h4>
                    <p>Add a transaction above to get started.</p>
                  </div>
                ) : (
                  <div className="wallet-transaction-list">
                    {transactions.map(txn => (
                      <div key={txn.id} className="wallet-transaction-item">
                        <div>
                          <div className="wallet-transaction-title">
                            {txn.type}
                            {txn.paymentMethod && (
                              <span className="wallet-transaction-method">
                                {txn.paymentMethod}
                              </span>
                            )}
                            {txn.status === 'pending' && (
                              <span className="wallet-transaction-status pending">Pending</span>
                            )}
                          </div>
                          <div className="wallet-transaction-desc">{txn.description}</div>
                          <div className="wallet-transaction-date">
                            {new Date(txn.createdAt).toLocaleString()}
                          </div>
                        </div>
                        <div className="wallet-transaction-actions">
                          {txn.status === 'pending' && txn.type === 'TOPUP' && txn.paymentMethod !== 'card' && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleApprove(txn.id)}
                              disabled={approving === txn.id}
                            >
                              {approving === txn.id ? 'Approving...' : 'Approve'}
                            </button>
                          )}
                          <div className={`wallet-transaction-amount ${txn.type}`}>
                            ${txn.amount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </>
            )
          ) : (
            <div className="wallet-empty">
              <div className="wallet-empty-icon">💳</div>
              <h3>Select a user</h3>
              <p>Choose a member to view balances and add transactions.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminWallets;
